import { useCallback, useEffect, useState } from 'react';
import type { IUpcomingFeature, IUpcomingFeatureUpdate } from '@skystone/shared';
import { formatPhaseLabel } from '@skystone/shared';
import { upcomingFeatureApi } from '@/services/upcoming-feature-api';
import {
  ArchivableListShell,
  type ArchivableColumn,
} from '@/components/admin/ArchivableListShell';
import { Badge } from '@/components/ui';
import { getErrorMessage } from '@/lib/utils';
import { UpcomingEditor } from './UpcomingEditor';

const ADMIN_LIST_LIMIT = 100;
const DEFAULT_DISPLAY_ORDER_INCREMENT = 10;

type EditorState =
  | { mode: 'closed' }
  | { mode: 'create' }
  | { mode: 'edit'; item: IUpcomingFeature };

function buildColumns(): ArchivableColumn<IUpcomingFeature>[] {
  return [
    {
      header: 'Order',
      cell: (item) => (
        <span className="text-sm tabular-nums text-muted-foreground">{item.displayOrder}</span>
      ),
      className: 'sm:max-w-[80px]',
    },
    {
      header: 'Phase',
      cell: (item) => (
        <Badge variant="secondary" className="uppercase tracking-wider">
          {formatPhaseLabel(item.phase)}
        </Badge>
      ),
      className: 'sm:max-w-[120px]',
    },
    {
      header: 'Title',
      cell: (item) => (
        <span className="block truncate text-sm font-medium" title={item.title}>
          {item.title}
        </span>
      ),
      className: 'sm:flex-[2]',
    },
    {
      header: 'Description',
      cell: (item) => (
        <span className="block truncate text-xs text-muted-foreground" title={item.description}>
          {item.description}
        </span>
      ),
      className: 'sm:flex-[3]',
    },
  ];
}

export function UpcomingTab() {
  const [items, setItems] = useState<IUpcomingFeature[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [editor, setEditor] = useState<EditorState>({ mode: 'closed' });

  const fetchItems = useCallback(async (includeArchived: boolean): Promise<void> => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await upcomingFeatureApi.getAll({
        includeArchived,
        limit: ADMIN_LIST_LIMIT,
      });
      setItems(data);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchItems(showArchived);
  }, [fetchItems, showArchived]);

  const handleArchiveToggle = async (item: IUpcomingFeature): Promise<void> => {
    try {
      await upcomingFeatureApi.setArchived(item.id, !item.archived);
      await fetchItems(showArchived);
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const handleSave = async (data: IUpcomingFeatureUpdate): Promise<void> => {
    if (editor.mode === 'edit') {
      await upcomingFeatureApi.update(editor.item.id, data);
    } else {
      await upcomingFeatureApi.create(data);
    }
    setEditor({ mode: 'closed' });
    await fetchItems(showArchived);
  };

  const editorOpen = editor.mode !== 'closed';
  const editorInitialItem = editor.mode === 'edit' ? editor.item : null;
  const maxOrder = items.reduce((max, item) => Math.max(max, item.displayOrder), 0);
  const defaultDisplayOrder = maxOrder + DEFAULT_DISPLAY_ORDER_INCREMENT;

  return (
    <>
      <ArchivableListShell<IUpcomingFeature>
        items={items}
        isLoading={isLoading}
        error={error}
        showArchived={showArchived}
        onShowArchivedChange={setShowArchived}
        onCreate={() => setEditor({ mode: 'create' })}
        onEdit={(item) => setEditor({ mode: 'edit', item })}
        onArchiveToggle={handleArchiveToggle}
        isArchived={(item) => item.archived}
        rowKey={(item) => item.id}
        columns={buildColumns()}
        createButtonLabel="Create upcoming item"
        emptyMessage="No upcoming features yet."
      />
      <UpcomingEditor
        open={editorOpen}
        initialItem={editorInitialItem}
        defaultDisplayOrder={defaultDisplayOrder}
        onClose={() => setEditor({ mode: 'closed' })}
        onSave={handleSave}
      />
    </>
  );
}
